import React, { useCallback, useEffect } from "react";
import {
  DropdownAction,
  DefaultColumnBuilder as builder,
} from "../components/ColumnBuilder";
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Layout,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  TablePaginationConfig,
  message,
} from "antd";
import { Content } from "antd/es/layout/layout";
import { PageHeader } from "@ant-design/pro-layout";
import {
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { SKAdNetwork } from "../../types/skadn";
import { WizardService } from "../../services/WizardService";
import { useSearchParams } from "react-router-dom";
import { TableColumnSelector } from "../components/TableColumnSelector";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/default-highlight";
import { theme } from "antd";
import {
  atomOneDark,
  atomOneLight,
} from "react-syntax-highlighter/dist/esm/styles/hljs";

const SKAdNetworkForm = (props: {
  network?: SKAdNetwork;
  isLoading?: boolean;
  onFinish: (network: SKAdNetwork) => void;
}) => {
  const [form] = Form.useForm();

  const onFinish = (values: any) => {
    const network: SKAdNetwork = {
      id: props.network?.id,
      ...values,
      ids: values.ids.split(", "),
    };
    form.resetFields();
    props.onFinish(network);
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      ...props.network,
      ids: props.network?.ids.join(", "),
    });
  }, [props.network, form]);

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="Status"
        name="status"
        rules={[{ required: true, message: "Please input status" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="IDs" name="ids">
        <Input.TextArea rows={6} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button loading={props.isLoading} type="primary" htmlType="submit">
            {props.network ? "Update" : "Create"}
          </Button>
          <Button htmlType="button" onClick={onReset}>
            Reset
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

const { useToken } = theme;

export const SKAdNetworkListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<SKAdNetwork[]>([]);
  const [selectedRecords, setSelectedRecords] = React.useState<number[]>([]);
  const [editing, setEditing] = React.useState<number | undefined>(undefined);
  const [modalVisible, setModalVisible] = React.useState<boolean>(false);
  const [code, setCode] = React.useState<string | undefined>();
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>([]);

  const api = WizardService.SKAdNetwork;
  const { token } = useToken();

  const isDarkTheme = token.colorBgBase === "#000";

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRecords(selectedRows.map((row) => row.key));
    },
  };

  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: searchParams.get("page") !== null ? +searchParams.get("page")! : 1,
    pageSize: searchParams.get("per") !== null ? +searchParams.get("per")! : 10,
    showSizeChanger: true,
  });

  const allColumns = [
    builder.id,
    builder.text("Status", "status", true),
    builder.array("SKAdNetwork IDs", "ids"),

    builder.dropdown(
      [DropdownAction.EDIT, DropdownAction.COPY, DropdownAction.DELETE],
      (id, action) => {
        console.log(id, action);
        switch (action) {
          case DropdownAction.EDIT:
            return editRecord(id);
          case DropdownAction.COPY:
            return copyRecord(id);
          case DropdownAction.DELETE:
            return removeRecord(id);
        }
      }
    ),
  ];

  const columns = () => {
    if (visibleColumns.length > 0) {
      return allColumns.filter(
        (c) => c.fixed === "right" || visibleColumns.includes(c.dataIndex?.toString() || "")
      );
    }
    return allColumns;
  };

  const removeRecord = (id: number) => {
    setIsLoading(true);
    api
      .delete(id)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeSelectedRecords = () => {
    setIsLoading(true);
    Promise.all(selectedRecords.map((id) => api.delete(id)))
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const saveRecord = (record: SKAdNetwork) => {
    setIsLoading(true);
    let promise: Promise<any>;
    console.log(record);
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .then(() => {
        setEditing(undefined);
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const showIntegration = () => {
    api
      .ids()
      .then((response) => {
        const dicts = response.map(
          (id) =>
            `\t<dict>\n\t\t<key>SKAdNetworkIdentifier</key>\n\t\t<string>${id}</string>\n\t</dict>`
        );
        return `<key>SKAdNetworkItems</key>\n<array>\n${dicts.join(
          "\n"
        )}\n</array>`;
      })
      .then((code) => setCode(code))
      .finally(() => setModalVisible(true));
  };

  const editRecord = (id: number) => {
    setEditing(id);
  };

  const copyRecord = (id: number) => {
    setIsLoading(true);
    api
      .copy(id)
      .then((copiedNetwork) => {
        return api.all(searchParams).then((response) => {
          setRecords(response.items || []);
          setPagination((state) => ({
            ...state,
            current: response.metadata.page,
            pageSize: response.metadata.per,
            total: response.metadata.total,
          }));
          setEditing(copiedNetwork.id);
        });
      })
      .catch((error) => {
        message.error(error.message);
        const originalRecord = records.find((r) => r.id === id);
        if (originalRecord) {
          const copiedRecord: SKAdNetwork = {
            ...originalRecord,
            id: undefined,
            // Modify the status to indicate it's a copy
            status: `${originalRecord.status} (Copy)`,
          };
          setRecords([...records, copiedRecord]);
          setEditing(undefined);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const sync = () => {
    setIsLoading(true);
    api
      .synchronize(searchParams)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  // Calbacks
  const fetch = useCallback(() => {
    setSelectedRecords([]);
    setIsLoading(true);
    api
      .all(searchParams)
      .then((response) => {
        setRecords(response.items || []);
        setPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  }, [api, searchParams]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <Layout>
      <Content>
        <PageHeader title="SKAdNetwork Identifiers">
          <Space>
            <TableColumnSelector
              columns={allColumns}
              onChange={setVisibleColumns}
              storageKey="skadn-table-columns"
            />
            <Button
              onClick={showIntegration}
              icon={<BulbOutlined  />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined  />}
              onClick={(_) => setEditing(0)}
            >
              Create
            </Button>
            <Button
              icon={<SyncOutlined  />}
              onClick={(_) => sync()}
            >
              Synchronize
            </Button>
            <Popconfirm
              title="Delete SKAdNetworks"
              description="Are you sure to delete selected mappings?"
              onConfirm={removeSelectedRecords}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined  />}
                disabled={selectedRecords.length === 0}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </PageHeader>
        <Row>
          <Col flex="100%">
            <Table
              scroll={{ x: true }}
              loading={isLoading}
              columns={columns()}
              pagination={pagination}
              onChange={(pagination) => {
                setPagination(pagination);
                const params = new URLSearchParams(searchParams);

                pagination.current
                  ? params.set("page", pagination.current.toString())
                  : params.delete("page");
                pagination.pageSize
                  ? params.set("per", pagination.pageSize.toString())
                  : params.delete("per");

                setSearchParams(params);
              }}
              dataSource={records.map((record) => ({
                ...record,
                key: record.id,
              }))}
              rowSelection={rowSelection}
            />
          </Col>
        </Row>
        <Modal
          open={modalVisible}
          okText="Copy"
          onOk={() => code && navigator.clipboard.writeText(code)}
          onCancel={() => setModalVisible(false)}
          width={800}
        >
          <SyntaxHighlighter
            style={isDarkTheme ? atomOneDark : atomOneLight}
            language="xml"
          >
            {code}
          </SyntaxHighlighter>
        </Modal>
        <Drawer
          title={
            editing !== undefined && editing > 0
              ? "Edit SKAdNetwork #" + editing
              : "Create a new SKAdNetwork"
          }
          width={600}
          onClose={(_) => setEditing(undefined)}
          open={editing !== undefined}
          styles={{
            body: {
              paddingBottom: 80,
            },
          }}
        >
          <SKAdNetworkForm
            network={records.find((r) => r.id === editing)}
            onFinish={saveRecord}
            isLoading={isLoading}
          />
        </Drawer>
      </Content>
    </Layout>
  );
};
